// The door tablet's home screen is the first step (redesign 2026-09-27): search and photos right
// there, several children picked at once, so there is no separate "find" or "brothers and
// sisters" screen. The on-screen keyboard opens from the search box (the OS keyboard would cover
// half the tablet); with it closed the grid gets the room.
import { useMemo, useState } from "react";
import { EmptyState, Icon, PhotoTile } from "@vavr/ui";
import { searchChildren, type Roster } from "@vavr/client";
import { usePhoto, type PhotoSource } from "./photos.ts";
import { ROWS } from "./flow/FindChild.tsx";
import type { Child } from "./flow/machine.ts";

const MAX_RESULTS = 24;

function Tile({ child, photos, room, selected, onToggle }: { child: Child; photos: PhotoSource; room?: string; selected: boolean; onToggle: () => void }) {
  const src = usePhoto(photos, child.photo_sha256);
  return <PhotoTile kind="child" name={`${child.first} ${child.last}`} detail={room} src={src} selected={selected} onSelect={onToggle} />;
}

export interface FinderProps {
  roster: Roster;
  photos: PhotoSource;
  selected: string[];
  onToggle: (childId: string) => void;
  /** Leave out the heading when the screen around it already asks the question. */
  bare?: boolean;
}

export function Finder({ roster, photos, selected, onToggle, bare }: FinderProps) {
  const [q, setQ] = useState("");
  const [room, setRoom] = useState<string | null>(null);
  const [keys, setKeys] = useState(false);
  const rooms = useMemo(() => new Map(roster.rooms.map((r) => [r.id, r.name])), [roster]);
  const active = useMemo(() => roster.children.filter((c) => c.status !== "withdrawn"), [roster]);
  const results = useMemo(() => {
    const list = q || room ? searchChildren({ ...roster, children: active }, q, room) : active;
    // Chosen children stay first, so they never scroll out of sight while the parent keeps looking.
    const chosen = new Set(selected);
    return [...list].sort((a, b) => Number(chosen.has(b.id)) - Number(chosen.has(a.id)) || a.first.localeCompare(b.first));
  }, [roster, active, q, room, selected]);
  const type = (ch: string) => setQ((x) => (x + ch).slice(0, 20));

  return (
    <section className={`pmk-finder ${keys ? "pmk-finder--keys" : ""}`} aria-label="Find your child">
      {!bare && <h1 className="pmk-finder__title">Who are you collecting? <span>Tap every child</span></h1>}
      <button type="button" className="pmk-search" aria-expanded={keys} onClick={() => setKeys((k) => !k)}>
        <Icon name="search" />
        <output aria-live="polite" aria-label="Letters typed">{q || <span className="pmk-search__hint">Type the first letters</span>}</output>
        <span className="pmk-search__toggle">{keys ? "Close keyboard" : "Keyboard"}</span>
      </button>
      {keys && (
        <div className="pmk-letters pmk-letters--home" role="group" aria-label="Letters">
          {ROWS.map((row) => (
            <div key={row} className="pmk-letters__row">
              {[...row].map((ch) => <button key={ch} type="button" className="pmk-letters__key" onClick={() => type(ch)}>{ch}</button>)}
              {row === "VWXYZ" && (
                <>
                  <button type="button" className="pmk-letters__key" onClick={() => setQ((x) => (x.endsWith(" ") || !x ? x : `${x} `))} aria-label="Space"><Icon name="minus" /></button>
                  <button type="button" className="pmk-letters__key" onClick={() => setQ((x) => x.slice(0, -1))} aria-label="Delete last letter"><Icon name="backspace" /></button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {!keys && roster.rooms.length > 1 && (
        <div className="pmk-chips" role="group" aria-label="Room">
          <button type="button" className="pmk-chip" aria-pressed={room === null} onClick={() => setRoom(null)}>All rooms</button>
          {roster.rooms.map((r) => (
            <button key={r.id} type="button" className="pmk-chip" aria-pressed={room === r.id} onClick={() => setRoom(room === r.id ? null : r.id)}>{r.name}</button>
          ))}
          {(q || room) && <button type="button" className="pmk-chip pmk-chip--clear" onClick={() => { setQ(""); setRoom(null); }}>Clear</button>}
        </div>
      )}
      {results.length ? (
        <div className="pmk-finder__grid">
          {results.slice(0, keys ? 4 : MAX_RESULTS).map((c) => (
            <Tile key={c.id} child={c} photos={photos} room={c.room_id ? rooms.get(c.room_id) : undefined}
              selected={selected.includes(c.id)} onToggle={() => onToggle(c.id)} />
          ))}
        </div>
      ) : (
        <EmptyState icon="search" title="No child with those letters" description="Check the spelling, or ask a member of staff." />
      )}
    </section>
  );
}
